import { EventEmitter, Injectable, PipeTransform } from '@angular/core';
import { Subject } from 'rxjs';
import { PaginationService } from '../pagination/pagination.service';
import { defaultValuePipe } from '../types/default-value-pipe';
import { IndexCheckbox } from './types/index-checkbox';
import { CheckboxComponent } from '../controls/checkbox/checkbox.component';

export interface ViewState {
	criteria: ColumnSortedEvent;
	currentPage: number;
	pageSize: number;
}

const VIEW_STATE_STORAGE_PREFIX = 'vs::';

@Injectable()
export abstract class SmartTableService extends PaginationService {
	private static _viewStates = new Map<string, ViewState>();

	private _columnSortedSource = new Subject<ColumnSortedEvent>();
	private _columnFilteredSource = new Subject<ColumnSortedEvent>();
	private _columnToggledSource = new Subject<any>();
	private _columnCountChanged = new Subject<number>();
	private _criteriaClearedSource = new Subject<void>();

	private _columns: Array<Column> = [];
	private _criteria: ColumnSortedEvent = { sortCols: [] };
	// @ts-ignore
	private _columnPresets: { id: string; columns: number[] }[];
	private _filterPresets: FilterPresets = {};
	private _dynamicFilterOptions: Record<string, FilterOption[]> = {};
	private _viewStateKey: string | undefined;

	protected _indexCheckboxes: IndexCheckbox[] = [];
	protected _loaded$: EventEmitter<any> = new EventEmitter<any>();

	protected constructor() {
		super();
	}

	public get columnSorted$() {
		return this._columnSortedSource.asObservable();
	}
	public get columnFiltered$() {
		return this._columnFilteredSource.asObservable();
	}
	public get columnToggled$() {
		return this._columnToggledSource.asObservable();
	}
	public get columnCountChanged$() {
		return this._columnCountChanged.asObservable();
	}
	public get criteriaCleared$() {
		return this._criteriaClearedSource.asObservable();
	}

	public get hasActiveCriteria(): boolean {
		return this._criteria.sortCols.some((sc) => sc.filterKey !== undefined && sc.filterKey !== '');
	}

	public get ASCENDING() {
		return 'ASC';
	}

	public get DESCENDING() {
		return 'DESC';
	}

	public get NONE() {
		return 'NONE';
	}

	public get DEFAULT_DIR() {
		return this.ASCENDING;
	}

	public get columns(): Column[] {
		return this._columns;
	}

	public get visibleColumns(): Column[] {
		return this._columns.filter((col) => !col.hide);
	}

	public get indexCheckboxes(): readonly IndexCheckbox[] {
		return Object.freeze(this._indexCheckboxes.map((indexCheckbox) => indexCheckbox));
	}

	public init() {
		super.init();
		// apply defaults
	}

	public checkAll(checked: boolean) {
		this._indexCheckboxes.forEach((checkbox) => (checkbox.checkbox.result = checked));
	}

	public pushCheckbox(indexCheckbox: IndexCheckbox) {
		const registeredCheckboxesIndexes = this._indexCheckboxes.map(({ index }) => index);

		if (registeredCheckboxesIndexes.includes(indexCheckbox?.index)) {
			this.popIndexCheckbox(indexCheckbox.index);
		}
		this._indexCheckboxes.push(indexCheckbox);
	}

	public clearCheckboxes() {
		this._indexCheckboxes = [];
	}

	public popIndexCheckbox(indexOf: number) {
		const filteredIndexCheckboxes = this._indexCheckboxes.filter(({ index }) => index !== indexOf);
		if (filteredIndexCheckboxes.length !== this._indexCheckboxes.length) {
			this._indexCheckboxes = filteredIndexCheckboxes;
			return true;
		}
		return false;
	}

	public updateCheckbox(index: number, checkbox: CheckboxComponent) {
		this._indexCheckboxes[index].checkbox = checkbox;
	}

	public getCriteriaAsFilters(): Criteria {
		return this.enforcePresetOnCriteria(this.criteriaToFilters(this._criteria));
	}

	public getParameters() {
		return {
			criteria: this.getCriteriaAsFilters(),
			pagination: this.pagination,
		};
	}

	public columnSorted(event: ColumnSortedEvent) {
		let clearedCriteria = [];

		const columnMatchesId = (col: Column, id: string) => {
			if (Array.isArray(col.field)) {
				return col.field.includes(id) || col.field.some((f) => id === '~' + f);
			}
			return col.field === id || id === '~' + col.field;
		};

		for (const ec of this._criteria.sortCols) {
			if (this._columns.find((col) => columnMatchesId(col, ec.id))) {
				clearedCriteria.push(ec);
			} else if (ec.filterKey !== undefined) {
				// @ts-ignore
				ec.dir = undefined;
				clearedCriteria.push(ec);
			}
		}

		this._criteria.sortCols = clearedCriteria;

		for (const ec of event.sortCols) {
			let found = false;
			for (const c of this._criteria.sortCols) {
				if (c.id === ec.id) {
					c.dir = ec.dir;
					found = true;
				}
			}
			if (!found) {
				this._criteria.sortCols.push(ec);
			}
		}

		this._columnSortedSource.next(event);
		this.saveState();
	}

	public columnFiltered(event: ColumnSortedEvent, remove: boolean = false) {
		event.sortCols = event.sortCols.map((sortCol) => {
			if (sortCol.displayed === undefined) {
				sortCol.displayed = true;
			}

			return sortCol;
		});

		if (event.sortCols.length === 0) {
			this._criteria.sortCols.splice(0, this._criteria.sortCols.length);
			return;
		}
		let toBeRemoved = [];
		for (const ec of event.sortCols) {
			let found = false;
			for (const c of this._criteria.sortCols) {
				if (c.id === ec.id) {
					if (!remove) {
						c.filterKey = ec.filterKey;
						found = true;
					} else {
						toBeRemoved.push(c.id);
					}
				}
			}
			if (!found && !remove) {
				this._criteria.sortCols.push(ec);
			}
		}
		toBeRemoved.forEach((cId) => {
			const index = this._criteria.sortCols.findIndex(({ id }) => id === cId);
			this._criteria.sortCols.splice(index, 1);
		});
		this.goToFirstPage();
		this._columnFilteredSource.next(event);
		this.saveState();
	}

	// @ts-ignore
	public sortByCriteria(criteria: ColumnSortedEvent, data) {
		// @ts-ignore
		return data.sort((a, b) => {
			return this.cascadeCriteria(a, b, criteria);
		});
	}

	public registerColumn(column: Partial<Column> & { label: string; field: string | string[] }) {
		let _field = column.field;
		if (!_field && column.label.length > 0) {
			_field = column.label.trim()[0].toLowerCase() + column.label.trim().slice(1).replace(' ', '');
		} else if (!_field) {
			_field = column.label.trim().toLowerCase();
		}

		if (column.valueTransformer) {
			this.setDefaultValuePipes(column.valueTransformer);
		}

		this._columns.push({
			hide: false,
			hideable: false,
			stackable: false,
			searchable: true,
			isImage: false,
			...column,
			field: _field,
			valueTransformer: column.valueTransformer ?? { field: _field, transformer: defaultValuePipe },
		} as Column);
		this._columnCountChanged.next(this._columns.length);
	}

	public setFilterPresets(presets: FilterPresets) {
		this._filterPresets = presets;
	}

	public getFilterOptions(columnField: string | string[]): FilterOption[] {
		const fields = Array.isArray(columnField) ? columnField : [columnField];
		const primaryField = fields[0];

		const column = this._columns.find((col) => {
			if (Array.isArray(col.field)) {
				return col.field[0] === primaryField;
			}
			return col.field === primaryField;
		});
		const staticOptions: FilterOption[] = column?.filterOptions || [];

		const dynamicOptions: FilterOption[] = [];
		for (const f of fields) {
			const opts = this._dynamicFilterOptions[f] || [];
			for (const opt of opts) {
				if (!dynamicOptions.some((d) => d.value === opt.value)) {
					dynamicOptions.push(opt);
				}
			}
		}

		if (staticOptions.length === 0) {
			return dynamicOptions;
		}
		if (dynamicOptions.length === 0) {
			return staticOptions;
		}

		const merged = [...staticOptions];
		for (const opt of dynamicOptions) {
			if (!merged.some((m) => m.value === opt.value)) {
				merged.push(opt);
			}
		}
		return merged;
	}

	public setDynamicFilterOptions(options: Record<string, FilterOption[]>) {
		for (const field of Object.keys(options)) {
			this._dynamicFilterOptions[field] = options[field];
		}
	}

	public registerColumnPreset(id: string, columns: string[]) {
		// TODO refactor for new column type
		/*if (!this.columnPresets) {
          this.columnPresets = [];
        }
        let preset = { id: id, columns: [] };
        let exists = false;
        for (let p of this.columnPresets) {
          if (p.id === id) {
            // @ts-ignore
            preset = p;
            exists = true;
          }
        }
        for (let col of columns) {
          for (let i = 0; i < this._columnTags.length; i++) {
            if (this._columnTags[i] === col || this._columnFields[i] === col) {
              // @ts-ignore
              preset.columns.push(i);
            }
          }
        }
        if (!exists) {
          this.columnPresets.push(preset);
        }*/
	}

	public applyColumnPreset(id: string) {
		// TODO refactor for new column type
		/*for (let i = 0; i < this._columnHide.length; i++) {
          this._columnHide[i] = true;
        }
        for (let p of this.columnPresets) {
          if (p.id === id ) {
            for (let col of p.columns) {
              if (this._columnHide.length > col) {
                this._columnHide[col] = false;
              }
            }
            return;
          }
        }*/
	}

	public isStackable(event: ColumnSortedEvent): boolean {
		let res: any = null;
		for (const sortCol of event.sortCols) {
			res = this._columns.find((col) => col.field === sortCol.id) || {};
			if (!!res && res.stackable) {
				return true;
			}
		}
		return res;
	}

	public clearAll(preserveSorts: boolean = false): void {
		if (preserveSorts) {
			this._criteria.sortCols = this._criteria.sortCols
				.filter((sc) => sc.dir === this.ASCENDING || sc.dir === this.DESCENDING)
				.map((sc) => ({ ...sc, filterKey: undefined }));
		} else {
			this.flushCriteria();
		}
		this.page = 0;
		this._criteriaClearedSource.next();
		this._columnFilteredSource.next({ sortCols: [] });
		this.saveState();
	}

	public flush() {
		super.flush();
		this.flushCriteria();
	}

	public flushCriteria() {
		this._criteria = { sortCols: [] };
	}

	public flushColumns() {
		this._columns = [];
		this._columnPresets = [];
	}

	public setViewStateKey(key: string) {
		this._viewStateKey = key;
		this.restoreState();
	}

	protected override onStateChanged(): void {
		this.saveState();
	}

	protected pruneOrphanedCriteria() {
		if (this._columns.length === 0 || this._criteria.sortCols.length === 0) {
			return;
		}
		this._criteria.sortCols = this._criteria.sortCols.filter((sc) =>
			this._columns.some((col) => {
				const fields = Array.isArray(col.field) ? col.field : [col.field];
				return fields.includes(sc.id) || fields.some((f) => sc.id === '~' + f);
			})
		);
		this.saveState();
	}

	private saveState() {
		if (!this._viewStateKey) {
			return;
		}
		const state: ViewState = {
			criteria: { sortCols: this._criteria.sortCols.map((sc) => ({ ...sc })) },
			currentPage: this.currentPage || 0,
			pageSize: this.pageSize || 10,
		};
		SmartTableService._viewStates.set(this._viewStateKey, state);
		try {
			sessionStorage.setItem(VIEW_STATE_STORAGE_PREFIX + this._viewStateKey, JSON.stringify(state));
		} catch (e) {
			// sessionStorage may be unavailable or full
		}
	}

	private restoreState() {
		if (!this._viewStateKey) {
			return;
		}

		let state = SmartTableService._viewStates.get(this._viewStateKey);

		if (!state) {
			try {
				const stored = sessionStorage.getItem(VIEW_STATE_STORAGE_PREFIX + this._viewStateKey);
				if (stored) {
					state = JSON.parse(stored);
				}
			} catch (e) {
				// sessionStorage may be unavailable or corrupted
			}
		}

		if (!state) {
			return;
		}

		this._criteria = { sortCols: state.criteria.sortCols.map((sc) => ({ ...sc })) };
		this.page = state.currentPage;
		this.pageSize = state.pageSize;
	}

	private criteriaToFilters(criteria: ColumnSortedEvent): Criteria {
		let res: Criteria = [];

		for (const cc of criteria.sortCols) {
			if (cc.filterKey && cc.filterKey.length > 0) {
				res.push({ filter: cc.id, value: cc.filterKey, displayed: cc.displayed });
			}
			if (cc.dir && (cc.dir === this.ASCENDING || cc.dir === this.DESCENDING)) {
				res.push({ order: cc.id, value: cc.dir, displayed: false });
			}
		}

		return res;
	}

	private enforcePresetOnCriteria(criteria: Criteria) {
		const _criteria = [...criteria];
		for (let presetFilter of Object.keys(this._filterPresets)) {
			// TODO make criteria a map to ensure unique filter keys
			const existingCriterion = _criteria.find((c) => c.filter === presetFilter);
			if (!!existingCriterion) {
				// override existing criterion
				existingCriterion.value = this._filterPresets[presetFilter]; // also enforces a filter to be unset if preset as such
			} else {
				_criteria.push({
					filter: presetFilter,
					value: this._filterPresets[presetFilter],
				});
			}
		}

		return _criteria;
	}

	private setDefaultValuePipes(valueTransformer: ValueTransformer) {
		if (!valueTransformer.transformer) {
			valueTransformer.transformer = defaultValuePipe;
		}

		if (!!valueTransformer.dependentTransformer) {
			this.setDefaultValuePipes(valueTransformer.dependentTransformer);
		}

		if (!!valueTransformer.argResolver) {
			this.setDefaultValuePipes(valueTransformer.argResolver);
		}
	}

	private goToFirstPage() {
		this.config({
			currentPage: 0,
			pageSize: this.pageSize,
			totalPages: this.totalPages,
			datasetSize: this.dataSize,
		});
	}

	// @ts-ignore
	private cascadeCriteria(a, b, criteria: ColumnSortedEvent) {
		let greaterThan = -1;
		for (let i = 0; i < criteria.sortCols.length; i++) {
			if (typeof a[criteria.sortCols[i].id] === 'string') {
				if (a[criteria.sortCols[i].id].localeCompare(b[criteria.sortCols[i].id]) === 0) {
					continue;
				} else {
					greaterThan = a[criteria.sortCols[i].id].localeCompare(b[criteria.sortCols[i].id]);
					if (criteria.sortCols[i].dir !== this.ASCENDING) {
						greaterThan *= -1;
					}
					break;
				}
			} else {
				if (a[criteria.sortCols[i].id] > b[criteria.sortCols[i].id]) {
					greaterThan = 1;
					if (criteria.sortCols[i].dir !== this.ASCENDING) {
						greaterThan *= -1;
					}
					break;
				} else if (a[criteria.sortCols[i].id] < b[criteria.sortCols[i].id]) {
					greaterThan = -1;
					if (criteria.sortCols[i].dir !== this.ASCENDING) {
						greaterThan *= -1;
					}
					break;
				}
				// (a[col] === b[col])
			}
		}
		return greaterThan;
	}
}

export interface ColumnSortedEvent {
	sortCols: ColumnCriterion[];
}

export interface ColumnCriterion {
	id: string;
	dir?: string;
	filterKey?: string;
	displayed?: boolean;
}

interface _Column {
	label: string;
	field: string | string[];
	hide: boolean;
	hideable: boolean;
	stackable: boolean;
	searchable: boolean;
	valueTransformer: ValueTransformer;
	isImage?: boolean;
	headerStyle?: Record<string, string>;
	options?: Record<string, any>;
	nullFilterAsOr?: boolean;
}

export type Column = _Column & Record<string, any>;

export interface ArgResolver extends ValueTransformer {
	argsWriteTo: string;
}

export interface ValueTransformer {
	field?: string;
	transformer?: PipeTransform;
	args?: any;
	context?: any;
	dependentTransformer?: ValueTransformer;
	argResolver?: ArgResolver;
}

export interface Criterion extends Record<string, any | undefined> {
	filter?: string;
	order?: string;
	value?: string;
	displayed?: boolean;
}

export type Criteria = Criterion[];

export type FilterPresets = Record<string, any>; // key = filter id, value = filter value;

export interface FilterOption {
	value: string;
	label?: string;
}
